<script setup lang="ts">
import { computed, defineEmits, onMounted, ref, defineProps } from "vue";
import { useOperationStore } from "@/stores/operation";
import { getFileData } from "@/libs/tools";
import PopupView from "@/components/PopupView.vue";
import PopupErrorView from "@/components/PopupErrorView.vue";
import LoadingPopupView from "@/components/LoadingPopupView.vue";
import { fetch } from "@/plugins/fetchPlugin";
import dayjs from "dayjs";
import "dayjs/locale/fr";

// eslint-disable-next-line no-undef
const props = defineProps({
  status: {
    type: String,
    required: true,
  },
  counterHome: {
    type: Object,
    required: false,
  },
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Teleport from "vue2-teleport";

const operationStore = useOperationStore();
const op = computed(() => operationStore.operationData);
const logo = ref<{ filename: string; uri: string } | undefined>(undefined);
const active = ref(false);
const error = ref(false);
const email = ref("");
const loading = ref(false);

const date_debut = computed(() =>
  op.value ? dayjs(op.value.date_debut).locale("fr").format("DD MMMM YYYY") : ""
);
const date_fin = computed(() =>
  op.value ? dayjs(op.value.date_fin).locale("fr").format("DD MMMM YYYY") : ""
);

const emit = defineEmits(["direction", "setview"]);

const bannerDirection = () => {
  emit("direction", "left");
};

const setView = (newPath: string) => {
  emit("setview", newPath);
};

//const fullOpen = () => {
//  emit("direction", "fullopen");
//};

//const slideToReset = () => {
//  emit("direction", "reset");
//};

const onSubmit = async () => {
  try {
    loading.value = true;
    if (!email.value) {
      loading.value = false;
      error.value = true;
      return;
    }

    const login = await fetch(
      `/api/operations-custom/fromscratch-platform/check`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          operation_id: operationStore?.operationData?._id,
          email: email.value,
        }),
      }
    );

    if (login && login?.id && login?.uid) {
      await fetch(`/api/v2/formdata/sendReminderEmail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          operationId: operationStore?.operationData?._id,
          email: email.value,
        }),
      });
      active.value = true;
      email.value = "";
      loading.value = false;
    } else {
      error.value = true;
      loading.value = false;
      return;
    }
  } catch (e) {
    error.value = true;
    loading.value = false;
    setTimeout(() => {
      emit("setview", "register");
      emit("direction", "left");
    }, 1000);
  }
};

onMounted(async () => {
  try {
    const files = operationStore?.filesData ?? [];
    logo.value = getFileData(files, "logo1");
  } catch (e) {
    console.error(e);
  }
});
</script>

<template>
  <div class="gabarit-login" v-if="op">
    <LoadingPopupView v-if="loading" />
    <Teleport to="body">
      <PopupErrorView v-if="error" @close="error = false">
        <p v-html="$t('login.confirm_error')" />
      </PopupErrorView>
      <PopupView v-if="active" @close="active = false">
        <div class="p-4 text-center">
          <i
            class="floating-div fa-regular text-secondary-bg fa-envelope text-[90px] m-auto"
          ></i>
          <h1 class="font-bold text-base text-center py-4">
            {{ $t("login.confirm_title") }}
          </h1>
          <p class="pb-2">
            {{ $t("login.confirm_text") }}
          </p>
        </div>
      </PopupView>
    </Teleport>
    <div class="h-full flex flex-col">
      <div v-if="logo" class="h-fit logo_wrapper my-6">
        <!--<img
          :src="logo.uri"
          class="m-auto w-fit max-w-[50%] max-h-44 self-center object-contain"
        />  -->

        <!--<a @click.prevent="fullOpen">full open</a><br />
        <a @click.prevent="slideToReset">reset</a> -->
      </div>
      <div
        v-if="props.status === 'open'"
        class="flex justify-center items-center"
      >
        <form
          class="flex flex-wrap justify-between w-4/5 m-auto"
          @submit.prevent="onSubmit"
        >
          <p
            v-if="!op?.custom?.titre_home || op?.custom?.titre_home == 'Titre'"
            class="step"
          >
            {{ $t("login.login_title") }}
          </p>
          <p class="step" v-else v-html="op?.custom?.titre_home" />
          <p class="mb-8 text-center">
            <span class="text-sm block mt-4 text-justify"
              >Ne manquez pas cette opportunité unique ! Inscrivez-vous dès
              maintenant et faites partie de l’expérience MATAÏ.
            </span>

            <strong class="text-base">Première inscription ?</strong>
            <a
              @click.prevent="bannerDirection"
              class="!text-secondary-bg font-semibold cursor-pointer hidden desktop:inline-block"
              >Créez votre compte et rejoignez la course</a
            >
            <a
              @click.prevent="setView('register')"
              class="!text-secondary-bg font-semibold cursor-pointer desktop:hidden inline-block"
              >Créez votre compte et rejoignez la course</a
            >
          </p>
          <p
            v-if="
              !op?.custom?.text_home || op?.custom?.text_home == 'Description'
            "
            class="w-full pb-4 text-sm text-justify"
          >
            {{ $t("login.login_description") }}
          </p>

          <p
            class="w-full pb-4 !text-sm"
            v-html="op?.custom?.text_home"
            v-else
          />

          <div class="w-full">
            <label class="font-semibold">{{ $t("login.login_label") }}</label>
            <input type="email" placeholder="Adresse email" v-model="email" />
            <span v-if="error" class="error">{{
              $t("login.login_error")
            }}</span>
          </div>

          <div class="builder-buttons py-4 w-full !justify-center">
            <button type="submit">
              <i class="fa-regular fa-paper-plane mr-2"></i>
              {{ $t("login.login_button") }}
            </button>
          </div>

          <p
            v-if="!counterHome || counterHome.value > 0"
            class="mt-4 text-sm text-center w-full mb-8"
          >
            <span
              v-if="
                counterHome &&
                counterHome.value > 0 &&
                counterHome.data.positiveText &&
                counterHome.data.positiveText != '<div><br></div>'
              "
              class="!text-sm block pt-[5px] italic bg-green-100 p-1 rounded mt-4 text-green-700 font-semibold border border-green-300"
              v-html="
                counterHome.data.positiveText.replace(
                  '[value]',
                  counterHome.value
                )
              "
            />
            <span
              v-if="
                counterHome &&
                counterHome.value <= 0 &&
                counterHome.data.negativeText &&
                counterHome.data.negativeText != '<div><br></div>'
              "
              class="!text-sm block pt-[5px] italic bg-red-100 p-1 rounded mt-4 text-red-700 font-semibold border border-red-300"
              v-html="
                counterHome.data.negativeText.replace(
                  '[value]',
                  counterHome.value
                )
              "
            >
            </span>
          </p>
          <p class="text-xs italic text-gray-500">
            <a
              href="https://oxoformdocs.fra1.digitaloceanspaces.com/6746f70c439fbc9c810444fd/reglement.pdf"
              target="_blank"
              ><sup>(1)</sup> voir le règlement</a
            >
          </p>
        </form>
      </div>

      <div
        v-if="props.status === 'offline'"
        class="flex justify-center items-center text-primary-text"
      >
        <div v-if="op && op.custom && op.custom.text_wait" />
        <div v-else class="text-center w-72 mt-12 text-lg leading-6">
          {{ $t("login.opdisabled") }}<br /><br />
          {{ $t("login.tryagain") }}
        </div>
      </div>

      <div
        v-if="props.status === 'finish'"
        class="flex justify-center items-center text-primary-text"
      >
        <div v-if="op && op.custom && op.custom.text_finish" />
        <div v-else class="text-center w-full mt-12 text-lg leading-6">
          {{ $t("login.opfinished") }} <br />
          <span class="font-bold text-lg">{{ date_fin }}.</span><br /><br />
          {{ $t("login.goodbye") }}
        </div>
      </div>

      <div
        v-if="props.status === 'wait'"
        class="flex justify-center items-center text-primary-text"
      >
        <div
          v-if="op && (op.text_wait || op?.custom?.text_wait)"
          class="text-center w-full mt-12 text-lg leading-6"
          v-html="op.text_wait || op?.custom?.text_wait"
        />
      </div>

      <div
        v-if="counterHome && counterHome.value <= 0"
        class="flex justify-center items-center text-sm text-primary-text"
      >
        <div class="w-96 text-justify mt-12 text-sm leading-4">
          {{ $t("login.overquota") }}
        </div>
      </div>
    </div>
  </div>
</template>
